import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertCircle,
    ArrowRight,
    CalendarClock,
    CalendarPlus,
    CheckCircle2,
    Clock,
    Phone,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const bucketFor = (days) => {
    if (days == null) return { key: "unknown", cls: "status-neutral", label: "No due date" };
    if (days < 7) return { key: "red", cls: "status-red", label: "Urgent" };
    if (days < 30) return { key: "amber", cls: "status-amber", label: "Soon" };
    return { key: "green", cls: "status-green", label: "OK" };
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

const toInputValue = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return "";
    }
};

const toDateInputValue = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } catch {
        return "";
    }
};

const toTimeInputValue = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const h = d.getHours();
        const m = d.getMinutes();
        if (h === 0 && m === 0) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(h)}:${pad(m)}`;
    } catch {
        return "";
    }
};

const Dashboard = () => {
    const [items, setItems] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [apptJob, setApptJob] = useState(null);
    const [apptDate, setApptDate] = useState("");
    const [apptTime, setApptTime] = useState("");
    const [timeTbc, setTimeTbc] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = () =>
        Promise.all([
            api.get("/jobs/due-soon", { params: { days: 60 } }),
            api.get("/bookings/pending"),
        ])
            .then(([d, b]) => {
                setItems(d.data);
                setBookings(b.data);
            })
            .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load"))
            .finally(() => setLoading(false));

    useEffect(() => {
        load();
    }, []);

    const buckets = useMemo(() => {
        const b = { red: 0, amber: 0, green: 0, all: items.length };
        items.forEach((it) => {
            const k = bucketFor(it.days_until_due).key;
            if (b[k] != null) b[k] += 1;
        });
        return b;
    }, [items]);

    const filtered = useMemo(() => {
        if (filter === "all") return items;
        return items.filter((it) => bucketFor(it.days_until_due).key === filter);
    }, [items, filter]);

    const complete = async (jobId) => {
        try {
            await api.post(`/jobs/${jobId}/complete`);
            toast.success("Job marked complete — next service scheduled");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const openAppt = (job) => {
        setApptJob(job);
        setApptDate(toDateInputValue(job.appointment_date));
        setApptTime(toTimeInputValue(job.appointment_date));
        setTimeTbc(!!job.appointment_time_tbc);
    };

    const saveAppt = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const value = apptDate ? (timeTbc || !apptTime ? apptDate : `${apptDate}T${apptTime}`) : null;
            await api.put(`/jobs/${apptJob.id}/appointment`, {
                appointment_date: value,
                time_tbc: value ? !!timeTbc || !apptTime : false,
            });
            toast.success("Appointment saved");
            setApptJob(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div>
                <p className="text-label">Landing</p>
                <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none">
                    Due Soon
                </h2>
                <p className="text-neutral-600 mt-2 max-w-xl">
                    Jobs with a next-service date in the next 60 days. Red = under a week. Amber = under a
                    month. Green = plenty of time.
                </p>
            </div>

            {bookings.length > 0 && (
                <div className="dh-card p-5" style={{ borderColor: "var(--dh-orange)", boxShadow: "6px 6px 0 var(--dh-orange)" }} data-testid="pending-bookings-panel">
                    <div className="flex items-center gap-2 mb-1">
                        <CalendarPlus className="text-[color:var(--dh-orange)]" size={20} />
                        <p className="text-label !text-[color:var(--dh-orange-hot)]">
                            {bookings.length} booking request{bookings.length === 1 ? "" : "s"} — call & book them in
                        </p>
                    </div>
                    <h3 className="font-display font-black text-2xl uppercase leading-tight">
                        Awaiting appointment
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1 mb-4">
                        These customers clicked <em>Book my service</em>. Ring them, agree a date, then set
                        the appointment here — the confirmation email fires automatically and Dragon Heat
                        will re-remind them 3 days and 1 day before.
                    </p>
                    <ul className="space-y-2">
                        {bookings.map((b) => (
                            <li
                                key={b.id}
                                data-testid={`pending-booking-${b.id}`}
                                className="border-2 border-[color:var(--dh-line)] bg-white p-3 flex flex-col sm:flex-row gap-3 sm:items-center"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-display font-black uppercase text-lg leading-tight truncate">
                                        {b.customer_name || "—"}
                                    </p>
                                    <p className="text-sm text-neutral-600 truncate">{b.property_address}</p>
                                    <p className="text-xs text-neutral-500 font-mono mt-0.5">
                                        Requested {fmtDate(b.booking_requested_at)} ·{" "}
                                        {b.customer_email || "no email"} ·{" "}
                                        {b.customer_mobile || "no phone"}
                                    </p>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {b.customer_mobile && (
                                        <a
                                            href={`tel:${b.customer_mobile.replace(/\s/g, "")}`}
                                            className="dh-btn"
                                            data-variant="ghost"
                                            data-testid={`booking-call-${b.id}`}
                                        >
                                            <Phone size={14} /> Call
                                        </a>
                                    )}
                                    <button
                                        className="dh-btn"
                                        onClick={() => openAppt(b)}
                                        data-testid={`booking-set-appt-${b.id}`}
                                    >
                                        <CalendarClock size={14} /> Set date
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { key: "all", label: "All", n: buckets.all, cls: "status-neutral" },
                    { key: "red", label: "Urgent < 7d", n: buckets.red, cls: "status-red" },
                    { key: "amber", label: "Soon < 30d", n: buckets.amber, cls: "status-amber" },
                    { key: "green", label: "OK ≥ 30d", n: buckets.green, cls: "status-green" },
                ].map((b) => (
                    <button
                        key={b.key}
                        data-testid={`bucket-${b.key}`}
                        onClick={() => setFilter(b.key)}
                        className="dh-card p-4 text-left"
                        style={filter === b.key ? { boxShadow: "6px 6px 0 var(--dh-orange)" } : {}}
                    >
                        <div className={`inline-block px-2 py-0.5 text-xs font-bold uppercase mb-2 ${b.cls}`}>
                            {b.label}
                        </div>
                        <p className="font-display font-black text-4xl leading-none">{b.n}</p>
                    </button>
                ))}
            </div>

            {loading ? (
                <p className="text-label">Loading jobs…</p>
            ) : filtered.length === 0 ? (
                <div className="dh-card p-8 text-center">
                    <CheckCircle2 className="mx-auto text-[color:var(--dh-green)]" size={40} />
                    <p className="font-display uppercase font-bold text-2xl mt-3">Nothing to chase</p>
                    <p className="text-neutral-600">No jobs match this filter.</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="due-soon-list">
                    {filtered.map((it) => {
                        const b = bucketFor(it.days_until_due);
                        const hasAppt = !!it.appointment_date;
                        return (
                            <div
                                key={it.id}
                                className="dh-card flex flex-col sm:flex-row overflow-hidden"
                                data-testid={`due-item-${it.id}`}
                            >
                                <div className={`${b.cls} sm:w-32 p-4 sm:p-5 flex sm:flex-col justify-between sm:justify-center items-start`}>
                                    <div>
                                        <p className="text-[10px] tracking-widest uppercase opacity-80 font-bold">
                                            {b.label}
                                        </p>
                                        <p className="font-display font-black text-3xl sm:text-4xl leading-none">
                                            {it.days_until_due < 0
                                                ? `${Math.abs(it.days_until_due)}d`
                                                : `${it.days_until_due}d`}
                                        </p>
                                        <p className="text-xs opacity-80">
                                            {it.days_until_due < 0 ? "overdue" : "to go"}
                                        </p>
                                    </div>
                                    <Clock className="sm:mt-4" size={20} />
                                </div>
                                <div className="flex-1 p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:items-center">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-label">{it.service_name || "Service"}</p>
                                        <p className="font-display font-black text-xl uppercase leading-tight truncate">
                                            {it.customer_name || "—"}
                                        </p>
                                        <p className="text-sm text-neutral-600 truncate">{it.property_address}</p>
                                        <p className="text-xs text-neutral-500 mt-1 font-mono">
                                            Due {fmtDate(it.next_due_date)} · {it.customer_email || "no email"}
                                        </p>
                                        {hasAppt && (
                                            <div className="mt-2 px-2 py-1 inline-flex items-center gap-1 status-green">
                                                <CalendarClock size={12} />
                                                <span className="text-[10px] uppercase font-bold tracking-wider">
                                                    Booked · {fmtDate(it.appointment_date)}
                                                    {it.appointment_time_tbc ? " · TBC" : ""}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <button
                                            data-testid={`due-set-appt-${it.id}`}
                                            className="dh-btn"
                                            data-variant={hasAppt ? "ghost" : undefined}
                                            onClick={() => openAppt(it)}
                                        >
                                            <CalendarClock size={16} />{" "}
                                            {hasAppt ? "Reschedule" : "Book in"}
                                        </button>
                                        <button
                                            data-testid={`due-complete-${it.id}`}
                                            className="dh-btn"
                                            data-variant="ghost"
                                            onClick={() => complete(it.id)}
                                        >
                                            <CheckCircle2 size={16} /> Done
                                        </button>
                                        <Link
                                            data-testid={`due-open-${it.id}`}
                                            to={`/properties/${it.property_id}`}
                                            className="dh-btn"
                                            data-variant="ghost"
                                        >
                                            Open <ArrowRight size={16} />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && items.some((it) => it.days_until_due < 0) && (
                <div className="dh-card p-4 flex gap-3 items-start" style={{ borderColor: "var(--dh-red)" }}>
                    <AlertCircle className="text-[color:var(--dh-red)] mt-0.5" size={20} />
                    <p className="text-sm">
                        Some jobs are past their next-service date. Book them in and mark complete to reset
                        the clock.
                    </p>
                </div>
            )}

            <Dialog open={!!apptJob} onOpenChange={(v) => !v && setApptJob(null)}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-tight text-2xl">
                            {apptJob?.appointment_date ? "Reschedule" : "Book in appointment"}
                        </DialogTitle>
                    </DialogHeader>
                    {apptJob && (
                        <form onSubmit={saveAppt} className="space-y-3" data-testid="dash-appt-form">
                            <div>
                                <p className="text-label">Customer</p>
                                <p className="font-display font-black uppercase text-lg leading-tight">
                                    {apptJob.customer_name}
                                </p>
                                <p className="text-sm text-neutral-600">{apptJob.property_address}</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-label block mb-1">Date</label>
                                    <input
                                        type="date"
                                        className="dh-input"
                                        data-testid="dash-appt-date"
                                        value={apptDate}
                                        onChange={(e) => setApptDate(e.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-label block mb-1">
                                        Time <span className="text-neutral-400 normal-case">(optional)</span>
                                    </label>
                                    <input
                                        type="time"
                                        className="dh-input"
                                        data-testid="dash-appt-time"
                                        value={apptTime}
                                        onChange={(e) => {
                                            setApptTime(e.target.value);
                                            if (e.target.value) setTimeTbc(false);
                                        }}
                                        disabled={timeTbc}
                                    />
                                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            data-testid="dash-appt-tbc"
                                            checked={timeTbc}
                                            onChange={(e) => {
                                                setTimeTbc(e.target.checked);
                                                if (e.target.checked) setApptTime("");
                                            }}
                                            className="h-4 w-4 accent-[color:var(--dh-orange)]"
                                        />
                                        <span className="text-xs uppercase tracking-widest font-bold">
                                            Time TBC
                                        </span>
                                    </label>
                                </div>
                            </div>
                            <p className="text-xs text-neutral-500 font-mono">
                                Leave time blank if TBC. Confirmation email fires straight away, plus
                                reminders 3 days and 1 day before. Guard rules still apply.
                            </p>
                            <DialogFooter>
                                <button
                                    type="submit"
                                    className="dh-btn"
                                    disabled={saving || !apptDate}
                                    data-testid="dash-appt-save"
                                >
                                    {saving ? "Saving…" : "Save appointment"}
                                </button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Dashboard;
